/**
 * Rekenwerk achter het productoverzicht: één status per product, groeperen,
 * en welke rijen een shift-klik precies raakt.
 *
 * Bewust los van React. Het zijn de regels waar je later over struikelt — welke
 * status wint, telt een ingeklapte groep mee in de selectie — en die wil je
 * kunnen nalezen en testen zonder een component te renderen.
 */

import { formatCurrency } from '@/lib/format';
import { BILLING_PERIOD_LABELS, type ProductListItem } from '@/types/product';

export type RowStatus =
  | 'archived'
  | 'review'
  | 'draft'
  | 'draft_open'
  | 'disabled'
  | 'published';

export interface RowStatusView {
  key: RowStatus;
  label: string;
  /** Tailwind-klasse voor de stip én de tekst; semantisch, niet decoratief. */
  tone: string;
}

export const ROW_STATUS: Record<RowStatus, RowStatusView> = {
  archived: { key: 'archived', label: 'Gearchiveerd', tone: 'text-muted-foreground' },
  review: { key: 'review', label: 'Nakijken', tone: 'text-[#B8600B]' },
  draft: { key: 'draft', label: 'Concept', tone: 'text-muted-foreground' },
  draft_open: { key: 'draft_open', label: 'Concept open', tone: 'text-[#0091AE]' },
  disabled: { key: 'disabled', label: 'Uitgeschakeld', tone: 'text-muted-foreground' },
  published: { key: 'published', label: 'Gepubliceerd', tone: 'text-[#00856A]' },
};

/**
 * Eén status per rij, in volgorde van wat je moet weten. Nakijken wint van
 * concept omdat het de actie is; uitgeschakeld staat ónder concept omdat de
 * schakelaar in de rij dat toch al laat zien.
 */
export function rowStatus(product: ProductListItem): RowStatus {
  if (product.archivedAt) return 'archived';
  if (product.needsReview) return 'review';
  if (!product.published) return 'draft';
  if (product.hasDraft) return 'draft_open';
  if (!product.enabled) return 'disabled';
  return 'published';
}

export type GroupBy = 'category' | 'status' | 'none';

export interface ProductGroup {
  key: string;
  label: string;
  products: ProductListItem[];
}

const NO_CATEGORY = 'Zonder categorie';

export function groupProducts(products: ProductListItem[], groupBy: GroupBy): ProductGroup[] {
  if (groupBy === 'none') {
    return [{ key: 'all', label: 'Alle producten', products }];
  }

  const map = new Map<string, ProductListItem[]>();
  for (const product of products) {
    const key =
      groupBy === 'category'
        ? product.category ?? NO_CATEGORY
        : ROW_STATUS[rowStatus(product)].label;
    map.set(key, [...(map.get(key) ?? []), product]);
  }

  // De groepen houden de volgorde waarin ze in de lijst opdoken. De server
  // levert de producten in sheetvolgorde, en die volgorde is een opbouw
  // (programma's eerst, losse opties later) die alfabetiseren kapotmaakt.
  // Bij groeperen op status is de volgorde van binnenkomst net zo goed als
  // elke andere.
  return [...map.entries()]
    .map(([key, items]) => ({ key, label: key, products: items }))
    // "Zonder categorie" hoort onderaan; het is een restgroep, geen categorie.
    .sort((a, b) => {
      if (a.key === NO_CATEGORY) return 1;
      if (b.key === NO_CATEGORY) return -1;
      return 0;
    });
}

/**
 * De id's in de volgorde waarin ze op het scherm staan, ingeklapte groepen
 * overgeslagen. Dit is de volgorde waarover een shift-klik een reeks kiest —
 * wat je niet ziet, selecteer je ook niet per ongeluk.
 */
export function visibleOrder(groups: ProductGroup[], collapsed: Set<string>): string[] {
  return groups
    .filter((group) => !collapsed.has(group.key))
    .flatMap((group) => group.products.map((product) => product.id));
}

/** De id's tussen twee rijen, grenzen inbegrepen. Leeg als er één ontbreekt. */
export function rangeBetween(order: string[], from: string, to: string): string[] {
  const start = order.indexOf(from);
  const end = order.indexOf(to);
  if (start === -1 || end === -1) return [];
  return order.slice(Math.min(start, end), Math.max(start, end) + 1);
}

export type GroupCheckState = 'none' | 'some' | 'all';

export function groupCheckState(group: ProductGroup, selected: Set<string>): GroupCheckState {
  if (!group.products.length) return 'none';
  let hits = 0;
  for (const product of group.products) if (selected.has(product.id)) hits += 1;
  if (hits === 0) return 'none';
  return hits === group.products.length ? 'all' : 'some';
}

/**
 * Waar de selectie uit bestaat, in de taal van de statuskolom. Staat in de
 * bulkbalk zodat je vóór het publiceren ziet of je 10 nakijkers of 10
 * gepubliceerde producten te pakken hebt.
 */
export function summarizeSelection(
  products: ProductListItem[],
  selected: Set<string>,
): Array<{ status: RowStatus; count: number }> {
  const counts = new Map<RowStatus, number>();
  for (const product of products) {
    if (!selected.has(product.id)) continue;
    const status = rowStatus(product);
    counts.set(status, (counts.get(status) ?? 0) + 1);
  }
  const order: RowStatus[] = ['review', 'draft', 'draft_open', 'published', 'disabled', 'archived'];
  return order
    .filter((status) => counts.has(status))
    .map((status) => ({ status, count: counts.get(status) ?? 0 }));
}

/** Statusstippen achter een ingeklapte groep, zodat inklappen niets verbergt. */
export function groupStatusSummary(
  group: ProductGroup,
): Array<{ status: RowStatus; count: number }> {
  const all = new Set(group.products.map((product) => product.id));
  return summarizeSelection(group.products, all);
}

export function groupPriceRange(group: ProductGroup): string {
  const prices = group.products
    .flatMap((product) => (product.priceRange ? [product.priceRange.minCents, product.priceRange.maxCents] : []));
  if (!prices.length) return '—';
  const min = Math.min(...prices);
  const max = Math.max(...prices);
  return min === max ? money(min) : `${money(min)} – ${money(max)}`;
}

/**
 * In een overzichtstabel is "€ 17.485" leesbaarder dan "€ 17.485,00", en past
 * een prijsbereik van twee bedragen wél op één regel. Centen die er zijn, tonen
 * we wel — alleen ronde bedragen verliezen hun nullen.
 */
const money = (cents: number) => {
  const formatted = formatCurrency(cents / 100);
  return cents % 100 === 0 ? formatted.replace(/,00$/, '') : formatted;
};

/** Per periode achter het bedrag, zodat € 520 p/4w niet als eenmalig leest. */
const PERIOD_SUFFIX: Record<string, string> = {
  one_time: '',
  four_weekly: ' p/4w',
  monthly: ' p/m',
  quarterly: ' p/kw',
  yearly: ' p/jr',
};

export function priceLabel(product: ProductListItem): string {
  if (!product.priceRange) return product.published ? 'Geen bedrag' : '—';
  const { minCents, maxCents } = product.priceRange;
  const suffix = PERIOD_SUFFIX[product.billingPeriod] ?? '';
  return minCents === maxCents
    ? `${money(minCents)}${suffix}`
    : `${money(minCents)} – ${money(maxCents)}${suffix}`;
}

export function metaLabel(product: ProductListItem): string {
  const parts = [BILLING_PERIOD_LABELS[product.billingPeriod]];
  if (product.unitLabel) parts.push(product.unitLabel);
  if (product.currentVersion) parts.push(`v${product.currentVersion}`);
  if (product.hasDraft) parts.push('concept open');
  if (product.labels.length) parts.push(product.labels.join(', '));
  return parts.join(' · ');
}

export function usageLabel(product: ProductListItem): string {
  if (!product.usageCount) return '—';
  return `${product.usageCount} regel${product.usageCount === 1 ? '' : 's'}`;
}

/**
 * Grof relatief, want in dit overzicht gaat het om "recent of niet" en niet om
 * het exacte moment. De precieze tijdstempel staat in de history van het product.
 */
export function relativeTime(iso: string, now: Date = new Date()): string {
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return '';
  const minutes = Math.max(0, Math.round((now.getTime() - then) / 60000));
  if (minutes < 60) return 'vandaag';
  const hours = Math.round(minutes / 60);
  if (hours < 24) return `${hours} u`;
  const days = Math.round(hours / 24);
  if (days < 14) return `${days} d`;
  const weeks = Math.round(days / 7);
  if (weeks < 9) return `${weeks} wk`;
  return `${Math.round(days / 30)} mnd`;
}

export function lastChangeLabel(product: ProductListItem, now?: Date): string {
  if (!product.lastChange) return '—';
  const when = relativeTime(product.lastChange.at, now);
  const who = product.lastChange.by;
  return who ? `${who}, ${when}` : when;
}
